/**
 * Shared planning-intent configuration and helper utility.
 * Captures rules for required, recommended, and optional fields,
 * CTA labels, and allowed sections based on user's planning focus selection.
 */

export const PLANNING_INTENT_CONFIGS = {
  everything: {
    key: 'everything',
    label: 'Full Party Plan',
    dashboardBadge: 'Full Plan',
    outputTitle: 'Your Full Party Plan',
    themeCardCta: 'Plan This Event',
    finalReviewCta: 'Generate Full Plan',
    generatePlanCta: 'Generate Full Plan',
    requiredFields: ['event_type', 'focus', 'guest_count', 'budget', 'country', 'zip'],
    recommendedFields: ['date', 'age_range', 'venue', 'food', 'activities', 'theme'],
    optionalFields: [],
    allowedPlanSections: ['overview', 'shopping', 'sourcing', 'timeline', 'budget', 'details', 'activities', 'menu', 'logistics', 'dress', 'colors'],
    allowedShoppingCategories: ['Decor', 'Food & Cake', 'Activities', 'Printables', 'Rentals', 'Party Supplies'],
    backendCategoryHints: ['Decor', 'Food & Cake', 'Activities', 'Printables', 'Rentals', 'Party Supplies']
  },
  theme_ideas: {
    key: 'theme_ideas',
    label: 'Theme Ideas',
    dashboardBadge: 'Theme Concept',
    outputTitle: 'Your Theme Concept Board',
    themeCardCta: 'Save Theme Idea',
    finalReviewCta: 'Generate Theme Ideas',
    generatePlanCta: 'Generate Theme Ideas',
    requiredFields: ['event_type', 'focus', 'theme_mode'],
    recommendedFields: ['age_range', 'theme_styles', 'additional_details'],
    optionalFields: ['guest_count', 'budget', 'date', 'country', 'zip'],
    allowedPlanSections: ['overview', 'colors', 'details'],
    allowedShoppingCategories: [],
    backendCategoryHints: ['Theme Concepts', 'Color Palettes', 'Moodboard Notes']
  },
  decor: {
    key: 'decor',
    label: 'Decor Plan',
    dashboardBadge: 'Decor Plan',
    outputTitle: 'Your Decor Plan',
    themeCardCta: 'Build Decor Plan',
    finalReviewCta: 'Generate Decor Plan',
    generatePlanCta: 'Generate Decor Plan',
    requiredFields: ['event_type', 'focus'],
    recommendedFields: ['budget', 'guest_count', 'venue', 'theme_styles', 'zip'],
    optionalFields: ['date', 'age_range'],
    allowedPlanSections: ['overview', 'shopping', 'sourcing', 'budget', 'details', 'logistics', 'colors'],
    allowedShoppingCategories: ['Decor', 'Rentals', 'Party Supplies', 'Printables'],
    backendCategoryHints: ['Decor', 'Rentals', 'Party Supplies', 'Printables']
  },
  food: {
    key: 'food',
    label: 'Food & Drinks Plan',
    dashboardBadge: 'Food Plan',
    outputTitle: 'Your Food & Drinks Plan',
    themeCardCta: 'Build Food Plan',
    finalReviewCta: 'Generate Food & Drinks Plan',
    generatePlanCta: 'Generate Food & Drinks Plan',
    requiredFields: ['event_type', 'focus', 'guest_count'],
    recommendedFields: ['budget', 'food_source', 'cuisine_types', 'meals', 'dietary', 'date'],
    optionalFields: ['zip', 'country'],
    allowedPlanSections: ['overview', 'shopping', 'sourcing', 'budget', 'menu', 'logistics'],
    allowedShoppingCategories: ['Food & Cake', 'Party Supplies'],
    backendCategoryHints: ['Food & Cake', 'Beverages', 'Desserts', 'Catering', 'Serving Supplies']
  },
  activities: {
    key: 'activities',
    label: 'Activities Plan',
    dashboardBadge: 'Activity Plan',
    outputTitle: 'Your Activities Plan',
    themeCardCta: 'Build Activity Plan',
    finalReviewCta: 'Generate Activities Plan',
    generatePlanCta: 'Generate Activities Plan',
    requiredFields: ['event_type', 'focus', 'guest_count'],
    recommendedFields: ['age_range', 'time', 'venue', 'budget'],
    optionalFields: ['date', 'zip'],
    allowedPlanSections: ['overview', 'shopping', 'sourcing', 'timeline', 'budget', 'activities', 'logistics'],
    allowedShoppingCategories: ['Activities', 'Party Supplies', 'Printables'],
    backendCategoryHints: ['Activities', 'Activity Supplies', 'Printables']
  },
  shopping: {
    key: 'shopping',
    label: 'Shopping List',
    dashboardBadge: 'Shopping List',
    outputTitle: 'Your Event Shopping List',
    themeCardCta: 'Build Shopping List',
    finalReviewCta: 'Generate Shopping List',
    generatePlanCta: 'Generate Shopping List',
    requiredFields: ['event_type', 'focus'],
    recommendedFields: ['budget', 'guest_count', 'zip', 'theme_styles'],
    optionalFields: ['date'],
    allowedPlanSections: ['overview', 'shopping', 'sourcing', 'budget'],
    allowedShoppingCategories: ['Decor', 'Food & Cake', 'Activities', 'Rentals', 'Party Supplies', 'Printables'],
    backendCategoryHints: ['Shopping List', 'Quantities', 'Estimates', 'Buy/Rent']
  }
};

const VALID_INTENTS = new Set([
  'everything',
  'theme_ideas',
  'decor',
  'food',
  'activities',
  'shopping'
]);

const labelMapping = {
  theme_ideas: 'Themes',
  decor: 'Decor',
  food: 'Food',
  activities: 'Activities',
  shopping: 'Shopping'
};

const formatListWithAmpersand = (list) => {
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} & ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
};

const union = (...arrays) => {
  const set = new Set();
  arrays.forEach(arr => {
    if (Array.isArray(arr)) {
      arr.forEach(item => set.add(item));
    }
  });
  return Array.from(set);
};

export const normalizeFocusSelection = (value) => {
  let rawList = [];
  if (Array.isArray(value)) {
    rawList = value;
  } else if (typeof value === 'string') {
    rawList = [value];
  }

  // Filter, trim, and validate values against the supported intents set
  const normalized = rawList
    .map(v => (typeof v === 'string' ? v.trim() : ''))
    .filter(v => v && VALID_INTENTS.has(v));

  if (normalized.length === 0 || normalized.includes('everything')) {
    return ['everything'];
  }

  return normalized;
};

export const getPrimaryPlanningIntent = (focusSelection) => {
  const normalized = normalizeFocusSelection(focusSelection);
  if (normalized.includes('everything')) {
    return 'everything';
  }
  if (normalized.length === 1) {
    return normalized[0];
  }
  return 'mixed';
};

export const getPlanningIntentConfig = (focusSelection) => {
  const normalized = normalizeFocusSelection(focusSelection);
  const primary = getPrimaryPlanningIntent(normalized);

  if (primary !== 'mixed') {
    return PLANNING_INTENT_CONFIGS[primary] || PLANNING_INTENT_CONFIGS.everything;
  }

  // Handle mixed combinations
  const subIntents = normalized.filter(id => id !== 'everything');
  const subConfigs = subIntents.map(id => PLANNING_INTENT_CONFIGS[id]).filter(Boolean);

  const requiredFields = union(...subConfigs.map(c => c.requiredFields));
  const allRecommended = union(...subConfigs.map(c => c.recommendedFields));
  const allOptional = union(...subConfigs.map(c => c.optionalFields));

  const recommendedFields = allRecommended.filter(f => !requiredFields.includes(f));
  const optionalFields = allOptional.filter(f => !requiredFields.includes(f) && !recommendedFields.includes(f));

  const allowedPlanSections = union(...subConfigs.map(c => c.allowedPlanSections));
  const allowedShoppingCategories = union(...subConfigs.map(c => c.allowedShoppingCategories));
  const backendCategoryHints = union(...subConfigs.map(c => c.backendCategoryHints));

  const selectedLabels = subIntents.map(id => labelMapping[id]).filter(Boolean);
  const joinedLabels = formatListWithAmpersand(selectedLabels) || 'Custom';

  return {
    key: 'mixed',
    label: 'Custom Plan',
    dashboardBadge: 'Custom Plan',
    outputTitle: 'Your Custom Party Plan',
    themeCardCta: `Build ${joinedLabels} Plan`,
    finalReviewCta: 'Generate Custom Plan',
    generatePlanCta: 'Generate Custom Plan',
    requiredFields,
    recommendedFields,
    optionalFields,
    allowedPlanSections,
    allowedShoppingCategories,
    backendCategoryHints
  };
};

export const isFieldRequiredForIntent = (focusSelection, field) => {
  const config = getPlanningIntentConfig(focusSelection);
  return config.requiredFields.includes(field);
};

export const isFieldRecommendedForIntent = (focusSelection, field) => {
  const config = getPlanningIntentConfig(focusSelection);
  return config.recommendedFields.includes(field);
};

export const getAllowedPlanSections = (focusSelection) => {
  const config = getPlanningIntentConfig(focusSelection);
  return config.allowedPlanSections;
};

export const getAllowedShoppingCategories = (focusSelection) => {
  const config = getPlanningIntentConfig(focusSelection);
  return config.allowedShoppingCategories;
};

export const getIntentCtaLabel = (focusSelection, context) => {
  const config = getPlanningIntentConfig(focusSelection);
  switch (context) {
    case 'themeCard':
      return config.themeCardCta;
    case 'finalReview':
      return config.finalReviewCta;
    case 'generatedPlan':
      return config.generatePlanCta;
    case 'dashboard':
      return config.dashboardBadge;
    default:
      return config.label;
  }
};

export const sanitizeEventInputForApi = (eventData) => {
  if (!eventData) return eventData;
  const clean = { ...eventData };

  // 1. Numeric optional fields
  const numericFields = ['budget', 'guest_count', 'theme_count'];
  numericFields.forEach(field => {
    const val = clean[field];
    if (val === '' || val === undefined || val === null) {
      clean[field] = null;
    } else {
      const parsed = parseInt(val, 10);
      clean[field] = Number.isFinite(parsed) ? parsed : null;
    }
  });

  const btp = clean['budget_tolerance_percent'];
  if (btp === '' || btp === undefined || btp === null) {
    clean['budget_tolerance_percent'] = 10;
  } else {
    const parsed = parseInt(btp, 10);
    clean['budget_tolerance_percent'] = Number.isFinite(parsed) ? parsed : 10;
  }

  // 2. List fields helper
  const cleanListField = (val) => {
    if (val === '' || val === null || val === undefined) return [];
    if (Array.isArray(val)) {
      return val.map(x => String(x || '').trim()).filter(Boolean);
    }
    if (typeof val === 'string') {
      return val.split(',').map(x => x.trim()).filter(Boolean);
    }
    return [];
  };

  const listFields = ['meals', 'dietary_restrictions', 'theme_styles', 'theme_modifiers', 'focus_selection', 'food_source', 'cuisine_types'];
  listFields.forEach(field => {
    if (field in clean || ['meals', 'dietary_restrictions', 'theme_styles', 'theme_modifiers', 'focus_selection'].includes(field)) {
      clean[field] = cleanListField(clean[field]);
    }
  });

  // 3. String optional fields
  const stringOptionalFields = ['zip_code', 'date', 'venue', 'venue_type', 'theme_input', 'additional_details', 'dietary_instructions', 'event_type', 'custom_event_type'];
  stringOptionalFields.forEach(field => {
    const val = clean[field];
    if (val === '' || val === undefined || val === null) {
      clean[field] = null;
    } else {
      clean[field] = String(val).trim() || null;
    }
  });

  const country = clean['country'];
  if (country === '' || country === undefined || country === null) {
    clean['country'] = 'US';
  } else {
    clean['country'] = String(country).trim() || 'US';
  }

  // 4. Union string/list fields
  const unionFields = ['age_range', 'time_of_day'];
  unionFields.forEach(field => {
    const val = clean[field];
    if (val === '' || val === undefined || val === null) {
      clean[field] = null;
    } else if (Array.isArray(val)) {
      const filtered = val.map(x => String(x || '').trim()).filter(Boolean);
      clean[field] = filtered.length > 0 ? filtered : null;
    } else {
      clean[field] = String(val).trim() || null;
    }
  });

  // 5. Dict fields
  const eventSpecifics = clean['event_specifics'];
  if (eventSpecifics === '' || eventSpecifics === null || eventSpecifics === undefined) {
    clean['event_specifics'] = {};
  } else if (typeof eventSpecifics === 'object' && !Array.isArray(eventSpecifics)) {
    clean['event_specifics'] = eventSpecifics;
  } else {
    clean['event_specifics'] = {};
  }

  // 6. Food menu structure dict
  const foodMenuStructure = clean['food_menu_structure'];
  if (foodMenuStructure && typeof foodMenuStructure === 'object' && !Array.isArray(foodMenuStructure)) {
    const sanitizedFms = {};
    const allowedMealSlugs = new Set([
      'breakfast', 'brunch', 'coffee_tea', 'lunch', 'dinner',
      'appetizers', 'finger_foods', 'snacks', 'desserts',
      'cocktails', 'late_night_bites'
    ]);
    const selectedMeals = Array.isArray(clean['meals']) ? clean['meals'] : [];
    const selectedMealsSet = new Set(selectedMeals);
    Object.entries(foodMenuStructure).forEach(([key, val]) => {
      if (allowedMealSlugs.has(key) && selectedMealsSet.has(key)) {
        const parsed = parseInt(val, 10);
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 5) {
          sanitizedFms[key] = parsed;
        }
      }
    });
    clean['food_menu_structure'] = Object.keys(sanitizedFms).length > 0 ? sanitizedFms : null;
  } else {
    clean['food_menu_structure'] = null;
  }

  return clean;
}
